import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Calendar, ChevronLeft, ChevronRight, Newspaper } from "lucide-react";
import { useNews, resolveImage, useSiteSettings } from "@/hooks/useCms";

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-PT", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function NewsBanner() {
  const { data: news, loading } = useNews();
  const settings = useSiteSettings();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  const total = news.length;

  const next = useCallback(() => {
    setIdx((i) => (total ? (i + 1) % total : 0));
  }, [total]);

  const prev = useCallback(() => {
    setIdx((i) => (total ? (i - 1 + total) % total : 0));
  }, [total]);

  useEffect(() => {
    if (paused || total <= 1) return undefined;
    if (settings.news_autoscroll_enabled === false) return undefined;
    const interval = Math.max(1000, Number(settings.news_autoscroll_ms) || 3000);
    const t = setInterval(next, interval);
    return () => clearInterval(t);
  }, [paused, total, next, settings.news_autoscroll_ms, settings.news_autoscroll_enabled]);

  if (loading || total === 0) return null;

  const item = news[idx];
  const img = resolveImage(item.image);

  return (
    <section
      className="py-16 lg:py-20 bg-[#0E1A2B] dark:bg-[#050810] text-white border-t border-white/5"
      data-testid="news-banner"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <p className="text-xs tracking-[0.24em] uppercase font-semibold text-[#4FC6D8] mb-3 inline-flex items-center gap-2">
              <Newspaper size={14} /> Notícias
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-white">
              Últimas da INXPEK.
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {total > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={prev}
                  aria-label="Anterior"
                  data-testid="news-prev"
                  className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-slate-300 hover:border-[#4FC6D8] hover:text-[#4FC6D8] active:scale-95 transition-all"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={next}
                  aria-label="Seguinte"
                  data-testid="news-next"
                  className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-slate-300 hover:border-[#4FC6D8] hover:text-[#4FC6D8] active:scale-95 transition-all"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
            <Link
              to="/noticias"
              className="inline-flex items-center gap-1 text-sm font-semibold text-[#4FC6D8] hover:gap-2 transition-all"
            >
              Ver todas <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm min-h-[300px]">
          <AnimatePresence mode="wait">
            <motion.article
              key={item.id || idx}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="grid md:grid-cols-2 min-h-[300px]"
              data-testid={`news-slide-${item.slug}`}
            >
              <div className="relative aspect-[16/10] md:aspect-auto md:h-full bg-white/5 overflow-hidden">
                {img ? (
                  <img src={img} alt={item.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-white/20">
                    <Newspaper size={56} strokeWidth={1.2} />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0E1A2B]/40 dark:to-[#050810]/40 md:from-black/10" />
              </div>
              <div className="p-8 lg:p-10 flex flex-col justify-center">
                {item.date && (
                  <p className="text-xs tracking-wider uppercase text-white/50 mb-3 inline-flex items-center gap-1.5">
                    <Calendar size={12} /> {formatDate(item.date)}
                  </p>
                )}
                <h3 className="font-heading text-2xl lg:text-3xl font-medium leading-tight mb-3 text-white">
                  {item.title}
                </h3>
                {item.excerpt && (
                  <p className="text-slate-300/85 leading-relaxed mb-5">
                    {item.excerpt}
                  </p>
                )}
                <Link
                  to={`/noticias/${item.slug}`}
                  data-testid={`news-cta-${item.slug}`}
                  className="group inline-flex items-center gap-2 text-sm font-semibold text-[#4FC6D8] self-start"
                >
                  Ler mais
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </motion.article>
          </AnimatePresence>

          {total > 1 && (
            <div className="absolute bottom-4 left-8 lg:left-[calc(50%+2.5rem)] flex items-center gap-1.5" data-testid="news-dots">
              {news.map((n, i) => (
                <button
                  key={n.id || i}
                  onClick={() => setIdx(i)}
                  aria-label={`Notícia ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? "w-8 bg-[#4FC6D8]" : "w-1.5 bg-white/20 hover:bg-white/40"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
